import type { RemoteResources, SyncOperation } from '../domain/resources';
import type { RepositoryStore } from '../data/repositories';
import type { ApiClient } from './api-client';

interface AuthResponse {
  authenticated: boolean;
}

interface PushResponse {
  ok: boolean;
  acceptedIds: string[];
}

interface PullResponse {
  ok: boolean;
  contractVersion: number;
  resources: RemoteResources;
}

export class SyncEngine {
  private paused = false;

  async pause(): Promise<void> {
    this.paused = true;
    await this.running;
  }

  resume(): void { this.paused = false; }

  private resyncRequested = false;
  private started = false;
  private running: Promise<void> | null = null;

  constructor(
    private readonly api: ApiClient,
    private readonly store: RepositoryStore
  ) {}

  start(): void {
    if (!this.api.available || this.started) return;
    this.started = true;
    let debounce: ReturnType<typeof setTimeout>;
    window.addEventListener('app:repository-change', event => {
      if ((event as CustomEvent).detail?.resourceType === 'cache') return;
      clearTimeout(debounce);
      debounce = setTimeout(() => void this.sync(), 800);
    });
    setInterval(() => {
      if (document.visibilityState === 'visible' && navigator.onLine) void this.sync();
    }, 30000);
    window.addEventListener('online', () => void this.sync());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.sync();
    });
    void this.sync();
  }

  sync(): Promise<void> {
    if (this.paused) return Promise.resolve();
    if (this.running) {
      this.resyncRequested = true;
      return this.running;
    }
    this.running = this.performSync()
      .catch(error => {
        console.warn('[sync] Synchronization deferred:', error);
        window.dispatchEvent(new CustomEvent('app:sync-error', { detail: { error } }));
      })
      .finally(() => {
        this.running = null;
        if (this.resyncRequested) {
          this.resyncRequested = false;
          void this.sync();
        }
      });
    return this.running;
  }

  private async performSync(): Promise<void> {
    if (!navigator.onLine) return;
    const auth = await this.api.json<AuthResponse>('/api/auth/me');
    if (!auth.authenticated) return;

    const pending = await this.store.pendingOperations();
    for (let offset = 0; offset < pending.length; offset += 200) {
      const operations = pending.slice(offset, offset + 200);
      const response = await this.push(operations);
      await this.store.acknowledgeOperations(response.acceptedIds);
    }

    const pulled = await this.api.json<PullResponse>('/api/sync/pull');
    if (pulled.contractVersion !== 1) {
      throw new Error(`Unsupported sync contract ${pulled.contractVersion}`);
    }
    await this.store.applyRemoteResources(pulled.resources || {});
    window.dispatchEvent(new CustomEvent('app:sync-complete'));
  }

  private push(operations: SyncOperation[]): Promise<PushResponse> {
    return this.api.json<PushResponse>('/api/sync/push', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contractVersion: 1, operations })
    });
  }
}
